import { useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';

export function useDebouncedQueue<T>(
  onImmediate: (item: T) => void,
  onDebouncedBatch: (items: T[]) => void | Promise<void>,
  delay: number,
) {
  const queueRef = useRef<T[]>([]);

  // Debounced function (runs only after the delay)
  const debounced = useRef(
    debounce(async () => {
      const batch = [...queueRef.current];
      queueRef.current = []; // clear queue

      if (batch.length > 0) {
        await onDebouncedBatch(batch);
      }
    }, delay),
  ).current;

  //  Main function
  const add = useCallback(
    (item: T) => {
      onImmediate(item); // runs every call
      queueRef.current.push(item); // add to queue
      debounced(); // restart debounce timer
    },
    [onImmediate, debounced],
  );

  const flush = useCallback(async () => {
    debounced.cancel();
    const batch = [...queueRef.current];
    queueRef.current = [];
    if (batch.length > 0) {
      await onDebouncedBatch(batch);
    }
  }, [onDebouncedBatch, debounced]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (queueRef.current.length > 0) {
        flush();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      if (queueRef.current.length > 0) {
        flush();
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flush]);

  return { add, flush };
}
